import React, { useEffect, useState } from "react";
import { useRouter } from "@tanstack/react-router";
import { usePathname } from '#app/lib/hooks/router-compat';
import {
  Box,
  Button,
  Collapsible,
  CollapsiblePanel,
  CollapsibleTrigger,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  ErrorBoundary,
  GridSpinner,
  Icon,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useIsMobile,
} from "@salestrak/ui";

import { reportRenderError } from "#app/lib/error-reporter";
import { useCopyFeedback } from "#app/lib/utils/copy-actions";
import { AnalyticsContext, NavigationEvent, trackEvent } from "#app/lib/analytics";

interface FallbackProps {
  error: Error;
  reset: () => void;
}

/** Which tier threw: the whole shell, or one screen inside a live shell. */
type ErrorScope = "app" | "screen";

/**
 * App-wide boundary used at the root layout. Renders a full-bleed fallback
 * when the entire tree throws. Resets automatically when the route changes
 * so users aren't trapped by a sticky error on navigation.
 */
export function AppErrorBoundary({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  return (
    <ErrorBoundary
      resetKey={pathname}
      onError={reportRenderError}
      fallback={(p) => <FullScreenErrorFallback {...p} />}
    >
      {children}
    </ErrorBoundary>
  );
}

/**
 * Adapter for the router's `defaultErrorComponent` (wired in `src/main.tsx`),
 * which supplies `{ error, reset }`. Renders the inline fallback so a failing
 * screen doesn't collapse the whole shell.
 */
export function RouteErrorBoundary({
  error,
  retry,
}: {
  error: Error;
  retry: () => void;
}) {
  return <InlineErrorFallback error={error} reset={retry} />;
}

/**
 * A retry that actually retries. Both boundaries hand us a `reset` that only
 * clears the boundary's own caught-error state, and on its own that re-renders
 * the same tree over the same data: a route whose loader threw is still in
 * error status, so the router's Match re-throws the very same error before a
 * frame paints, and a component that threw over stale data throws again. The
 * button looked dead. `router.invalidate()` is the router's documented retry:
 * it flips errored matches back to pending, re-runs every loader and rebuilds
 * the match objects, which is also the key the route boundary resets on.
 * Invalidate FIRST, so the reset re-renders a pending screen, not the error.
 */
function useRetry(reset: () => void) {
  const router = useRouter();
  return () => {
    void router.invalidate();
    reset();
  };
}

// ---------------------------------------------------------------------------
// Fallback UIs
// ---------------------------------------------------------------------------
//
// The header speaks in prose and the raw error message sits below it as a
// quiet mono line — human copy and machine text never share a register. The
// message renders in exactly one place: the stack well below shows frames
// only, so an expanded trace never repeats the line above it.

function FullScreenErrorFallback({ error, reset }: FallbackProps) {
  return (
    <Box
      fill="flex"
      background="background"
      align="center"
      justify="center"
      padding="lg"
      role="alert"
    >
      <ErrorScreen error={error} reset={reset} scope="app" />
    </Box>
  );
}

function InlineErrorFallback({ error, reset }: FallbackProps) {
  return (
    <Box fill="flex" padding="lg" align="center" justify="center" role="alert">
      <ErrorScreen error={error} reset={reset} scope="screen" />
    </Box>
  );
}

const SCOPE_COPY: Record<ErrorScope, { title: string; description: string }> = {
  app: {
    title: "Something went wrong",
    description:
      "We hit an unexpected error. Try again, or reload the app if it keeps happening.",
  },
  screen: {
    title: "This screen hit an error",
    description: "Something went wrong loading this view.",
  },
};

/** `Empty` centres and clamps its own prose to reading width, so this column
 *  exists for the trace well below it: stack frames are long machine text and
 *  wrapping them at prose width destroys the one thing they are read for. */
const TRACE_WIDTH = 840;

function ErrorScreen({
  error,
  reset,
  scope,
}: FallbackProps & { scope: ErrorScope }) {
  const copy = SCOPE_COPY[scope];
  // Once per error shown. The error's class name only: a message can carry
  // user data.
  useEffect(() => {
    trackEvent({
      context: AnalyticsContext.Navigation,
      event: NavigationEvent.ErrorScreen,
      data: { scope, errorName: error.name },
    });
  }, [error, scope]);
  const router = useRouter();
  const retry = useRetry(reset);
  // `back` only escapes when there is somewhere IN THE APP to go. The router's
  // history counts its own entries, which is the question; `window.history`
  // counts the whole tab, so a cold load out of the sign-in redirect or an
  // OAuth round-trip said "yes" and then stepped out of the app, or back onto
  // a page that bounced straight forward into the same error. A dead button
  // is worse than none, so those cases offer a reload instead.
  const canGoBack = scope === "screen" && router.history.canGoBack();
  const goBack = () => router.history.back();

  return (
    <Box direction="column" maxWidth={TRACE_WIDTH} width="100%">
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            {/* Decorative, not a status: the default "Loading" live region
                would misreport an error screen to assistive tech. */}
            <GridSpinner
              animation="checkerboard"
              aria-hidden
              color="destructive"
              size="lg"
            />
          </EmptyMedia>
          <EmptyTitle>{copy.title}</EmptyTitle>
          <EmptyDescription>{copy.description}</EmptyDescription>
          <ErrorMessageLine message={error.message} />
        </EmptyHeader>
        <EmptyContent>
          <Box direction="row" gap="sm" justify="center" width="100%" wrap>
            <Button onClick={retry}>Try again</Button>
            {canGoBack ? (
              <Button onClick={goBack} variant="outline">
                Go back
              </Button>
            ) : (
              <Button onClick={reloadApp} variant="outline">
                Reload app
              </Button>
            )}
          </Box>
        </EmptyContent>
      </Empty>
      <ErrorDetails error={error} />
    </Box>
  );
}

/** The raw error message, set apart from the prose as a quiet mono line. */
function ErrorMessageLine({ message }: { message: string }) {
  if (!message) return null;
  return (
    <Box paddingTop="sm" width="100%">
      <Text mono size="xs" title={message} truncate={3} variant="faint">
        {message}
      </Text>
    </Box>
  );
}

/**
 * The stack well, plus the copy affordance in every build.
 *
 * The trace itself is dev-only, but "copy what happened" is the one action
 * that survives to production: it is how a rep hands a support thread
 * something actionable instead of retyping a sentence from a screenshot.
 */
function ErrorDetails({ error }: { error: Error }) {
  const isMobile = useIsMobile();
  // Open on a desktop viewport: the trace is the reason a developer is on
  // this screen at all. Small viewports start folded — the well would push
  // the actions off-screen. Initial state only; a resize never re-folds it.
  const [open, setOpen] = useState(!isMobile);
  const { copied, copy } = useCopyFeedback();
  const frames = stackFrames(error);
  const showTrace = import.meta.env.DEV && frames.length > 0;

  // Two channels of feedback: the toast is the one every copy action in the
  // app speaks, and the button's own check flip confirms WHICH control acted
  // — the toast lands in a corner, the check lands under the pointer. The
  // helper carries the `execCommand` fallback, so a blocked Clipboard API
  // (insecure origin, unfocused document) still copies instead of failing
  // silently.
  const copyDetails = () => {
    trackEvent({
      context: AnalyticsContext.Navigation,
      event: NavigationEvent.CopyErrorDetails,
    });
    return copy(diagnostics(error), "Error details");
  };

  const copyIcon = <Icon name={copied ? "check" : "copy"} size="xs" />;
  const copyLabel = copied ? "Copied" : "Copy error details";

  if (!showTrace) {
    return (
      <Box direction="row" justify="center" width="100%">
        <Button onClick={() => void copyDetails()} size="sm" variant="ghost">
          {copyIcon}
          {copyLabel}
        </Button>
      </Box>
    );
  }

  return (
    <Collapsible onOpenChange={setOpen} open={open}>
      {/* The trace is a panel RESTING on the page, so it wears `card` — the
          widget-card treatment: hairline + the card drop carry the edge. The
          borderless `sunken` it started as read as a smudge in light mode,
          and a dark re-root was tried and read too intense there. */}
      <Box
        border
        direction="column"
        overflow="hidden"
        rounded="lg"
        surface="card"
        width="100%"
      >
        {/* The well's own header bar is the disclosure. A standalone
            "Show details" button above the well competed with the primary
            action for the same centered slot. */}
        <Box
          align="center"
          direction="row"
          gap="xs"
          justify="between"
          padding="xs"
        >
          <CollapsibleTrigger render={<Button size="xs" variant="ghost" />}>
            <Icon
              color="subtle"
              name={open ? "chevron-down" : "chevron-right"}
              size="xs"
            />
            <Text size={null} variant="muted" weight="medium">
              Stack trace
            </Text>
          </CollapsibleTrigger>
          {/* Icon-only, so the tooltip names the action — and flips with
              the icon, so a hovering pointer reads the outcome in place. */}
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  aria-label={copyLabel}
                  onClick={() => void copyDetails()}
                  size="icon-xs"
                  variant="ghost"
                />
              }
            >
              {copyIcon}
            </TooltipTrigger>
            <TooltipPopup side="top">{copyLabel}</TooltipPopup>
          </Tooltip>
        </Box>
        <CollapsiblePanel>
          {/* The rule rides the panel, so it exists only while open. The
              trace itself is a detail well recessed INSIDE the card — the
              header band stays on the card fill, the machine text sits one
              step down, so the two registers never share a ground. */}
          <Box
            border={["top"]}
            maxHeight={260}
            overflow="auto"
            padding="base"
            surface="sunken"
          >
            <Box direction="column">
              {frames.map((line, index) => (
                <StackFrameLine key={index} line={line} />
              ))}
            </Box>
          </Box>
        </CollapsiblePanel>
      </Box>
    </Collapsible>
  );
}

/** `at <function> (<location>)` — the shape of a V8 frame with a name. */
const FRAME_RE = /^\s*at\s+(.+?)\s+\((.+)\)\s*$/;

/**
 * One stack frame, set so the signal outweighs the noise: the function name
 * is the only reason anyone reads a frame, so it carries full ink — heavier
 * when the frame is the APP'S own code — while the `at` and the long
 * bundler-URL location stay faint. A React-internals frame is all noise and
 * renders quiet end to end.
 */
function StackFrameLine({ line }: { line: string }) {
  const match = FRAME_RE.exec(line);
  const fn = match?.[1];
  const location = match?.[2];
  if (!fn || !location) {
    // A nameless frame (`at http://…`) or a foreign engine format — nothing
    // to elevate, so the whole line stays quiet. `pre` elements keep the
    // line unwrapped; the well scrolls sideways instead of folding a URL.
    return (
      <Text as="pre" mono size="xs" variant="faint">
        {line.trim()}
      </Text>
    );
  }
  const isAppFrame = !location.includes("node_modules");
  return (
    <Text as="pre" mono size="xs" variant="faint">
      {"at "}
      <Text
        as="span"
        mono
        size={null}
        variant={isAppFrame ? undefined : "muted"}
        weight={isAppFrame ? "medium" : undefined}
      >
        {fn}
      </Text>
      {` (${location})`}
    </Text>
  );
}

/**
 * The frames of a stack, without its leading `Error: <message>` header —
 * `ErrorMessageLine` already carries that, and repeating it wastes the first
 * line of the well. Engines that format frames differently (Firefox's
 * `fn@url`) match nothing, so they fall through to the whole stack's lines.
 */
function stackFrames(error: Error): string[] {
  const stack = error.stack;
  if (!stack) return [];
  const frames = stack.split("\n").filter((line) => /^\s+at\s/.test(line));
  return frames.length > 0 ? frames : stack.split("\n").filter(Boolean);
}

/**
 * A paste-ready report: where it happened and when, then the raw stack. Read
 * from `window.location` rather than the router, because a fallback for a
 * tree that just threw cannot assume router context is intact.
 */
function diagnostics(error: Error): string {
  const where =
    typeof window === "undefined"
      ? "unknown"
      : window.location.pathname + window.location.search;
  return [
    `Path: ${where}`,
    `Time: ${new Date().toISOString()}`,
    `Agent: ${typeof navigator === "undefined" ? "unknown" : navigator.userAgent}`,
    "",
    error.stack ?? `${error.name}: ${error.message}`,
  ].join("\n");
}

function reloadApp() {
  if (typeof window !== "undefined") {
    window.location.reload();
  }
}
